import React, { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  ClickAwayListener,
  TextField,
  Typography,
  Grid,
  Paper,
  MenuItem,
  FormHelperText,
  DialogActions,
  Tooltip,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import { CheckCircle, Room } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { GoogleMap, Marker } from '@react-google-maps/api';

import AddressModal from 'component/Common/Mapfiles/AddressModal';
import WalkScoreModal from 'component/Common/Mapfiles/Walkscoremodal';
import { getMarkerIcon } from 'component/Common/Mapfiles/Mapelements';
import InfoIcon from '@mui/icons-material/Info';
import { textboxmargin } from './PropertyCreation.style';
import location from 'assets/icons/location.svg';

const PropertyAddressField = ({
  latitude,
  setLatitude,

  longitude,
  setLongitude,

  selectedWalkScores,
  setSelectedWalkScores,

  unitNumber,
  setUnitNumber,

  streetNumber,
  setStreetNumber,

  streetName,
  setStreetName,

  selectedStreetType,
  setSelectedStreetType,

  selectedStreetTypeabbrev,
  setSelectedStreetTypeabbrev,

  town,
  setTown,

  selectedState,
  setSelectedState,

  postCode,
  setPostCode,

  options,

  setErrorforaddress,
  errorforaddress,

  addressref,

  fromlegal,
  fromsoli,

  solicitorAddress = '',
}) => {
  const theme = useTheme();
  const matchesmd = useMediaQuery(theme.breakpoints.down('md'));

  const [lastSelectedAddress, setLastSelectedAddress] = useState('');
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [propertyAddress, setPropertyAddress] = useState('');
  const [walkScoreModalOpen, setWalkScoreModalOpen] = useState(false);
  const [isaddresschanged, setIsaddresschanged] = useState(false);
  const [openSuggestions, setOpenSuggestions] = useState(false);

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressInput, setAddressInput] = useState('');
  const [addressValidated, setAddressValidated] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showChooseOnMap, setShowChooseOnMap] = useState(false);

  const [mapDialogOpen, setMapDialogOpen] = useState(false);

  const [mapMarker, setMapMarker] = useState(null);
  const [mapLocationError, setMapLocationError] = useState(false);

  const [validatedAddress, setValidatedAddress] = useState(null);
  const [markerChanged, setMarkerChanged] = useState(false);

  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  const ignoreNextFocus = useRef(false);

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();

      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
    }
  }, []);

  useEffect(() => {
    if (!solicitorAddress) return;

    if (!window.google?.maps) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      {
        address: solicitorAddress,
        componentRestrictions: {
          country: 'AU',
        },
      },
      (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results.length) {
          handleSelectAddress({
            place_id: results[0].place_id,
          });
        }
      }
    );
  }, [solicitorAddress]);

  useEffect(() => {
    if (streetName || streetNumber || town || postCode || selectedState) {
      const displayAddress = [
        [
          unitNumber
            ? `${unitNumber}${streetNumber ? '/' : ''}${streetNumber}`
            : streetNumber,
          streetName,
          selectedStreetType,
        ]
          .filter(Boolean)
          .join(' '),
        town,
        postCode,
        selectedState,
      ]
        .filter(Boolean)
        .join(', ');

      setAddressInput(displayAddress);
    }
  }, [
    unitNumber,
    streetNumber,
    streetName,
    selectedStreetType,
    town,
    selectedState,
    postCode,
  ]);

  const searchAddress = (value) => {
    setAddressInput(value);

    // Remove validation once user edits
    setAddressValidated(false);

    if (!value.trim()) {
      setAddressSuggestions([
        {
          isMapOption: true,
          description: 'Choose location on map',
        },
      ]);
      return;
    }

    autocompleteService.current.getPlacePredictions(
      {
        input: value,
        componentRestrictions: {
          country: 'au',
        },
      },
      (results, status) => {
        // No results
        if (!results || results.length === 0) {
          setAddressSuggestions([
            {
              isNoResult: true,
              description: 'No address found',
            },
            {
              isMapOption: true,
              description: 'Choose location on map',
            },
          ]);
          return;
        }

        // Results found
        const sortedResults = [...results].sort((a, b) => {
          const aIsNSW =
            a.description.includes('NSW') ||
            a.structured_formatting?.secondary_text?.includes('NSW');

          const bIsNSW =
            b.description.includes('NSW') ||
            b.structured_formatting?.secondary_text?.includes('NSW');

          if (aIsNSW && !bIsNSW) return -1;
          if (!aIsNSW && bIsNSW) return 1;

          return 0; // preserve Google's ranking within the same state
        });
        const cleanedResults = sortedResults.map((item) => ({
          ...item,
          description: item.description.replace(/,\s*Australia$/i, ''),
        }));

        setAddressSuggestions([
          ...cleanedResults,
          {
            isMapOption: true,
            description: 'Choose location on map',
          },
        ]);
        setOpenSuggestions(true);
      }
    );
  };

  const handleSelectAddress = (option) => {
    if (!option) return;

    placesService.current.getDetails(
      {
        placeId: option.place_id,
        fields: ['formatted_address', 'geometry', 'address_components'],
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
          return;
        }

        const components = place.address_components;

        const getComponent = (type, short = false) => {
          const comp = components.find((c) => c.types.includes(type));

          return comp ? (short ? comp.short_name : comp.long_name) : '';
        };
        const route = getComponent('route');
        const streetNumber = getComponent('street_number');
        const unit = getComponent('subpremise');
        const town = getComponent('locality') || getComponent('sublocality');
        const state = getComponent('administrative_area_level_1', true);
        const postcode = getComponent('postal_code');
        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();
        let streetName = route || '';
        let streetType = '';
        let streetTypeabbrev = '';

        if (route) {
          const matchedStreetType = (options || []).find((item) => {
            const full = item.label?.toUpperCase();
            const abbr = item.streetAbbrev?.toUpperCase();

            return (
              route.toUpperCase().endsWith(` ${full}`) ||
              (abbr && route.toUpperCase().endsWith(` ${abbr}`))
            );
          });

          if (matchedStreetType) {
            streetType = matchedStreetType.label;
            streetTypeabbrev = matchedStreetType.streetAbbrev;

            const fullType = matchedStreetType.label?.trim();
            const abbrType = matchedStreetType.streetAbbrev?.trim();

            streetName = route;

            if (
              fullType &&
              streetName.toUpperCase().endsWith(` ${fullType.toUpperCase()}`)
            ) {
              streetName = streetName.slice(0, -(fullType.length + 1)).trim();
            } else if (
              abbrType &&
              streetName.toUpperCase().endsWith(` ${abbrType.toUpperCase()}`)
            ) {
              streetName = streetName.slice(0, -(abbrType.length + 1)).trim();
            }
          }
        }

        setUnitNumber(unit);

        setStreetNumber(streetNumber);

        setStreetName(streetName);

        setSelectedStreetType(streetType);

        setSelectedStreetTypeabbrev?.(streetTypeabbrev);

        setTown(town);

        setPostCode(postcode);

        setSelectedState(state);

        setLatitude?.(latitude);

        setLongitude?.(longitude);

        setPropertyAddress(place.formatted_address);

        const displayAddress = [
          [
            unit
              ? `${unit}${streetNumber ? '/' : ''}${streetNumber}`
              : streetNumber,
            streetName,
            streetType,
          ]
            .filter(Boolean)
            .join(' '),
          town,
          postcode,
          state,
        ]
          .filter(Boolean)
          .join(', ');

        setAddressInput(displayAddress);

        setPropertyAddress(displayAddress);

        setSelectedPlace(place);

        setLastSelectedAddress(displayAddress);

        setAddressValidated(true);

        setErrorforaddress?.({
          helperText: '',
          error: false,
        });

        let validaddress = {
          formattedAddress: place.formatted_address,

          unitNo: unit,

          streetNumber,

          streetName,

          streetType,

          town,

          state,

          postcode,

          latitude,

          longitude,
        };
        setValidatedAddress(validaddress);

        if (!mapDialogOpen && !fromlegal) {
          const isChanged =
            propertyAddress &&
            JSON.stringify(propertyAddress) !==
              JSON.stringify(validaddress.formattedAddress);

          setWalkScoreModalOpen(true);
          setIsaddresschanged(isChanged);
        }
      }
    );
  };
  const handleConfirmMapLocation = () => {
    if (!mapMarker) {
      setMapLocationError(true);
      return;
    }

    // Clear validation error
    setMapLocationError(false);

    // User didn't move the marker
    if (!markerChanged) {
      setMapDialogOpen(false);
      setIsAddressModalOpen(true);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      {
        location: mapMarker,
      },
      (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          populateAddressFromMap(results[0]);
        }
      }
    );
  };

  const populateAddressFromMap = (result) => {
    const components = result.address_components;
    const get = (type, short = false) => {
      const comp = components.find((c) => c.types.includes(type));
      return comp ? (short ? comp.short_name : comp.long_name) : '';
    };

    const streetNumber = get('street_number');
    const route = get('route');
    const suburb = get('locality') || get('sublocality');
    const postcode = get('postal_code');
    const state = get('administrative_area_level_1', true);

    let streetName = route || '';
    let streetType = '';
    let streetTypeabbrev = '';

    if (route) {
      const matchedStreetType = (options || []).find((item) => {
        const full = item.label?.toUpperCase();
        const abbr = item.streetAbbrev?.toUpperCase();

        return (
          route.toUpperCase().endsWith(` ${full}`) ||
          (abbr && route.toUpperCase().endsWith(` ${abbr}`))
        );
      });

      if (matchedStreetType) {
        streetType = matchedStreetType.label;
        streetTypeabbrev = matchedStreetType.streetAbbrev;

        streetName = route
          .replace(new RegExp(`\\s${matchedStreetType.label}$`, 'i'), '')
          .replace(new RegExp(`\\s${matchedStreetType.streetAbbrev}$`, 'i'), '')
          .trim();
      }
    }

    const unit = get('subpremise') || get('premise');

    setUnitNumber(unit);
    setStreetNumber(streetNumber);
    setStreetName(streetName);
    setSelectedStreetType(streetType);
    setSelectedStreetTypeabbrev?.(streetTypeabbrev);
    setTown(suburb);
    setPostCode(postcode);
    setSelectedState(state);

    setLatitude(mapMarker.lat);
    setLongitude(mapMarker.lng);

    setMapDialogOpen(false);
    setIsAddressModalOpen(true);
  };

  const validateTypedAddress = () => {
    if (!addressInput.trim()) {
      setAddressValidated(false);

      return;
    }

    autocompleteService.current.getPlacePredictions(
      {
        input: addressInput,

        componentRestrictions: {
          country: 'au',
        },
      },

      (results) => {
        if (!results || results.length === 0) {
          setAddressValidated(false);

          return;
        }

        const exactResult = results.find(
          (item) =>
            item.description.toLowerCase() === addressInput.toLowerCase()
        );

        if (exactResult) {
          handleSelectAddress(exactResult);
        } else {
          setAddressValidated(false);
        }
      }
    );
  };

  const openMapFromCurrentAddress = () => {
    setMarkerChanged(false);

    // If we already know the coordinates, use them
    if (latitude && longitude) {
      setMapMarker({
        lat: Number(latitude),
        lng: Number(longitude),
      });

      setMapDialogOpen(true);
      return;
    }

    // No address entered
    if (!addressInput?.trim()) {
      setMapMarker(null);
      setMapDialogOpen(true);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      {
        address: addressInput,
        componentRestrictions: {
          country: 'AU',
        },
      },
      (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results.length) {
          const location = results[0].geometry.location;

          setMapMarker({
            lat: location.lat(),
            lng: location.lng(),
          });
        } else {
          // Could not resolve address
          setMapMarker(null);
        }

        setMapDialogOpen(true);
      }
    );
  };

  const updateMarkerLocation = (lat, lng) => {
    setMapLocationError(false);
    setMarkerChanged(true);
    setMapMarker({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      {
        location: { lat, lng },
      },
      (results, status) => {
        if (status === 'OK' && results.length) {
          // Find the best result with a place_id
          const resultWithPlaceId =
            results.find((r) => r.place_id) || results[0];

          if (resultWithPlaceId?.place_id) {
            handleSelectAddress({
              place_id: resultWithPlaceId.place_id,
            });
          }
        }
      }
    );
  };

  const handleBlur = () => {
    if (lastSelectedAddress) {
      if (addressInput !== lastSelectedAddress) {
        setAddressInput(lastSelectedAddress);
        setAddressValidated(true);
      }
    } else {
      setAddressInput('');
      setAddressValidated(false);
    }
  };

  const handleWalkScoreClose = () => {
    setWalkScoreModalOpen(false);

    if (isaddresschanged || selectedWalkScores.length < 1) {
      // Clear temporary address
      setUnitNumber('');
      setStreetNumber('');
      setStreetName('');
      setSelectedStreetType('');
      setSelectedStreetTypeabbrev?.('');
      setTown('');
      setPostCode('');
      setSelectedState('');
      setLatitude?.(null);
      setLongitude?.(null);

      // Clear address UI
      setAddressInput('');
      setSelectedPlace(null);
      setAddressSuggestions([]);
      setAddressValidated(false);
      setValidatedAddress(null);

      // Add these
      setPropertyAddress('');
      setLastSelectedAddress('');

      // Clear walkscore
      setSelectedWalkScores?.([]);

      setIsaddresschanged(false);
    }
  };

  const handleAddressModalClose = () => {
    setIsAddressModalOpen(false);
    // User closed without Save -> clear everything
    setUnitNumber('');
    setStreetNumber('');
    setStreetName('');
    setSelectedStreetType('');
    setSelectedStreetTypeabbrev?.('');
    setTown('');
    setPostCode('');
    setSelectedState('');
    setLatitude?.(null);
    setLongitude?.(null);

    setAddressInput('');
    setSelectedPlace(null);
    setAddressSuggestions([]);
    setAddressValidated(false);
    setValidatedAddress(null);

    setPropertyAddress('');
    setLastSelectedAddress('');
    setSelectedWalkScores?.([]);
    setIsaddresschanged(false);
  };

  const handleAddressSave = (completedAddress) => {
    ignoreNextFocus.current = true;

    // Existing states
    setUnitNumber(completedAddress.unitNo || '');
    setStreetNumber(completedAddress.streetNumber || '');
    setStreetName(completedAddress.streetName || '');
    setSelectedStreetType(completedAddress.selectedStreetType || '');
    setSelectedStreetTypeabbrev?.(
      completedAddress.selectedStreetTypeabbrev || ''
    );
    setTown(completedAddress.town || '');
    setPostCode(completedAddress.postCode || '');
    setSelectedState(completedAddress.selectedState || '');
    setLatitude?.(completedAddress.latitude || null);
    setLongitude?.(completedAddress.longitude || null);
    setSelectedWalkScores?.(completedAddress.selectedWalkScores || []);

    // Save complete object
    setPropertyAddress(completedAddress);

    const formattedAddress = [
      [
        completedAddress.unitNo
          ? `${completedAddress.unitNo}${
              completedAddress.streetNumber ? '/' : ''
            }${completedAddress.streetNumber}`
          : completedAddress.streetNumber,
        completedAddress.streetName,
        completedAddress.selectedStreetType,
      ]
        .filter(Boolean)
        .join(' '),
      completedAddress.town,
      completedAddress.postCode,
      completedAddress.selectedState,
    ]
      .filter(Boolean)
      .join(', ');
    // ⭐ Update Google Autocomplete states
    setAddressInput(formattedAddress);

    setLastSelectedAddress(formattedAddress);

    setSelectedPlace({
      description: formattedAddress,
    });

    setAddressValidated(true);

    setValidatedAddress(completedAddress);

    setAddressSuggestions([]);

    setErrorforaddress?.({
      helperText: '',
      error: false,
    });

    setIsAddressModalOpen(false);
  };

  return (
    <>
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'row',
          position: fromsoli ? 'relative' : 'static',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
            }}
          >
            <TextField
              fullWidth
              autoComplete="off"
              ref={addressref || null}
              inputRef={addressref || null}
              error={errorforaddress.error}
              value={
                addressInput !== ''
                  ? addressInput ||
                    [
                      [
                        unitNumber
                          ? `${unitNumber}${
                              streetNumber ? '/' : ''
                            }${streetNumber}`
                          : streetNumber,
                        streetName,
                        selectedStreetType,
                      ]
                        .filter(Boolean)
                        .join(' '),
                      town,
                      postCode,
                      selectedState,
                    ]
                      .filter(Boolean)
                      .join(', ')
                  : ''
              }
              onChange={(e) => {
                const value = e.target.value;

                setAddressInput(value);

                searchAddress(value);

                if (value.trim()) {
                  setOpenSuggestions(true);
                } else {
                  setOpenSuggestions(false);
                }
              }}
              onFocus={() => {
                if (addressSuggestions.length > 0) {
                  setOpenSuggestions(true);
                }
              }}
              onBlur={handleBlur}
              placeholder={
                fromlegal
                  ? 'Click here to enter office address'
                  : 'Click to enter address'
              }
              sx={{
                ...textboxmargin,
                mt: '10px',
                '& .MuiInputBase-root': {
                  height: '56px',
                  fontFamily: 'Poppins',
                },
                '& .MuiInputBase-input': {
                  fontFamily: 'Poppins',
                },
                '& input': {
                  fontFamily: 'Poppins',
                },
              }}
              InputProps={{
                startAdornment: fromsoli && (
                  <InputAdornment position="start">
                    <img
                      alt="img"
                      src={location}
                      style={{
                        background: 'transparent',
                        paddingRight: '10px',
                        borderRight: '1px solid black',
                        width: matchesmd ? '25px' : '40px',
                        height: matchesmd ? '20px' : '',
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setOpenSuggestions(false);
                        openMapFromCurrentAddress();
                      }}
                    >
                      <Room
                        sx={{
                          color: '#6C2AD2',
                        }}
                      />
                    </IconButton>
                  </>
                ),
              }}
            />

            {openSuggestions && (
              <ClickAwayListener onClickAway={() => setOpenSuggestions(false)}>
                <Paper
                  elevation={6}
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    mt: 0.5,
                    zIndex: 99999,
                    maxHeight: 350,
                    overflowY: 'auto',
                    borderRadius: 2,
                  }}
                >
                  {addressSuggestions.map((option, index) => {
                    if (option.isNoResult) {
                      return (
                        <Box
                          key={index}
                          sx={{
                            p: 2,
                            borderBottom: '1px solid #EAEAEA',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 600,
                            }}
                          >
                            No matching address found
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 12,
                              color: '#6B7280',
                              mt: 0.5,
                            }}
                          >
                            Try refining your search or choose a location on the
                            map.
                          </Typography>
                        </Box>
                      );
                    }

                    if (option.isMapOption) {
                      return (
                        <MenuItem
                          key="map"
                          onClick={() => {
                            setOpenSuggestions(false);
                            openMapFromCurrentAddress();
                          }}
                          sx={{
                            borderTop: '1px solid #ddd',
                            fontWeight: 600,
                          }}
                        >
                          <Room
                            sx={{
                              mr: 1,
                              color: '#2E7D32',
                            }}
                          />
                          Choose location on map
                        </MenuItem>
                      );
                    }

                    return (
                      <MenuItem
                        key={option.place_id}
                        onClick={() => {
                          handleSelectAddress(option);

                          setAddressInput(option.description);

                          setOpenSuggestions(false);
                        }}
                      >
                        <Room
                          sx={{
                            mr: 1,
                            color: '#965CF0',
                          }}
                        />

                        {option.description}
                      </MenuItem>
                    );
                  })}
                </Paper>
              </ClickAwayListener>
            )}
          </Box>

          {errorforaddress.error && (
            <FormHelperText
              style={{
                color: '#d32f2f',
                fontFamily: 'poppins',
                textAlign: 'left',
                width: '100%',
              }}
            >
              {errorforaddress.helperText}
              {!fromlegal &&
                errorforaddress.helperText !==
                  'Address already registered. Please use a different address.' && (
                  <Tooltip
                    title="Please review the property type and ensure the address is suitable"
                    arrow
                  >
                    <InfoIcon
                      style={{
                        fontSize: '16px',
                        color: '#d32f2f',
                        cursor: 'pointer',
                        marginLeft: '5px',
                      }}
                    />
                  </Tooltip>
                )}
            </FormHelperText>
          )}
        </Box>
        <Box
          sx={{
            ...(fromsoli
              ? {
                  position: 'absolute',
                  top: '25px',
                  right: '-40px',
                  pointerEvents: 'none',
                }
              : {
                  width: '10px',
                  ml: '20px',
                  mt: '10px',
                  flexShrink: 0,
                }),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            visibility: addressValidated ? 'visible' : 'hidden',
          }}
        >
          <CheckCircle
            sx={{
              color: '#2E7D32',
              fontSize: 30,
            }}
          />
        </Box>
      </Grid>
      <WalkScoreModal
        open={walkScoreModalOpen}
        onClose={() => {
          handleWalkScoreClose();
        }}
        onConfirm={(e) => {
          setSelectedWalkScores?.(e);
          setWalkScoreModalOpen(false);
        }}
        addressDetails={validatedAddress}
        selectedwalkscore={selectedWalkScores}
        isChanged={isaddresschanged}
      />

      {/* Target Address Modal Placement */}
      <AddressModal
        open={isAddressModalOpen}
        onClose={() => {
          handleAddressModalClose();
        }}
        initialData={{
          unitNo: unitNumber,
          streetNumber,
          streetName,
          selectedStreetType,
          town,
          postCode,
          selectedState,
          latitude,
          longitude,
        }}
        streetType={options}
        onSave={handleAddressSave}
        isneedWalkscore={fromlegal ? false : true}
        setIsAddressModalOpen={setIsAddressModalOpen}
      />

      {/* MAP VIEW */}

      <Dialog
        open={mapDialogOpen}
        onClose={() => setMapDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh', // increase popup height
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'poppins' }}>
          Select Property Location
        </DialogTitle>

        <DialogContent
          sx={{
            p: 2,
            display: 'flex',
            flex: 1,
          }}
        >
          <GoogleMap
            zoom={16}
            center={
              mapMarker || {
                lat: -33.8688,
                lng: 151.2093,
              }
            }
            mapContainerStyle={{
              width: '100%',
              height: '100%',
            }}
            onClick={(e) => {
              updateMarkerLocation(e.latLng.lat(), e.latLng.lng());
            }}
          >
            {mapMarker && (
              <Marker
                position={mapMarker}
                icon={getMarkerIcon()}
                draggable={true}
                onDragEnd={(e) => {
                  updateMarkerLocation(e.latLng.lat(), e.latLng.lng());
                }}
              />
            )}
          </GoogleMap>
        </DialogContent>

        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            pb: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            {mapLocationError && (
              <Typography
                sx={{
                  color: '#d32f2f',
                  fontSize: '14px',
                  fontFamily: 'Poppins',
                  fontWeight: 500,
                }}
              >
                Please mark a location on the map before confirming.
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              style={{ textTransform: 'none' }}
              onClick={() => {
                setMapDialogOpen(false);
                setMapLocationError(false);

                setUnitNumber('');
                setStreetNumber('');
                setStreetName('');
                setSelectedStreetType('');
                setSelectedStreetTypeabbrev?.('');
                setTown('');
                setPostCode('');
                setSelectedState('');
                setLatitude?.(null);
                setLongitude?.(null);

                setAddressInput('');
                setSelectedPlace(null);
                setAddressSuggestions([]);
                setAddressValidated(false);
                setValidatedAddress(null);

                setPropertyAddress('');
                setLastSelectedAddress('');
                setSelectedWalkScores?.([]);
                setIsaddresschanged(false);
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleConfirmMapLocation}
              style={{ textTransform: 'none' }}
            >
              Confirm
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PropertyAddressField;
